import type { api } from "@healthos/schemas";
import type {
  ConfounderDuration,
  ConfounderReason,
  ConnectionType,
  ConnectionWho,
} from "@healthos/shared";
import { cn } from "@healthos/ui";
import {
  Barbell,
  BowlFood,
  CaretDown,
  Coffee,
  Drop,
  FirstAid,
  HandPalm,
  Lightning,
  Moon,
  PersonSimpleRun,
  PersonSimpleWalk,
  type Icon as PhosphorIcon,
  Pulse,
  Smiley,
  UsersThree,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { MEDICATIONS, medicationCadence, medicationDoses } from "../lib/glp1-format";
import {
  ChoiceField,
  type ChoiceOption,
  DateTimeField,
  NumberField,
  RangeField,
  SelectField,
  TextAreaField,
  TextField,
} from "./manual-signal-log-fields";
import {
  CONFOUNDER_DURATION_OPTIONS,
  CONFOUNDER_REASON_OPTIONS,
  CONNECTION_TYPE_OPTIONS,
  CONNECTION_WHO_OPTIONS,
  createManualLogForm,
  type ManualLogActionId,
  type ManualLogForm,
} from "./manual-signal-log-model";
import {
  type ManualLogQuickPick,
  ManualLogQuickPicks,
  manualLogQuickPicks,
  manualLogWorkoutMoreQuickPicks,
} from "./manual-signal-log-quick-picks";
import { VitalsFields } from "./manual-signal-log-vital-fields";

/** Icon per log action, shared by the log picker, headers, and edit modal. */
export const ACTION_ICONS: Record<ManualLogActionId, PhosphorIcon> = {
  food: BowlFood,
  hydration: Drop,
  sleep: Moon,
  mood: Smiley,
  symptoms: FirstAid,
  caffeine: Coffee,
  workout: Barbell,
  medication: Pulse,
  vitals: Pulse,
  disruptor: HandPalm,
  connection: UsersThree,
};

export type FormPatch = Partial<ManualLogForm>;

const WORKOUT_INTENSITY_OPTIONS: ChoiceOption[] = [
  { value: "easy", label: "Easy", caption: "Talk", Icon: PersonSimpleWalk },
  { value: "moderate", label: "Moderate", caption: "Steady", Icon: PersonSimpleRun },
  { value: "hard", label: "Hard", caption: "Breathless", Icon: Lightning },
];

const MEDICATION_STATUS_OPTIONS: ChoiceOption[] = [
  { value: "taken", label: "Taken" },
  { value: "skipped", label: "Skipped" },
  { value: "delayed", label: "Delayed" },
  { value: "moved", label: "Moved" },
  { value: "note", label: "Note" },
];

const PRIMARY_PICK_LABELS: Record<ManualLogActionId, string> = {
  food: "Meal",
  hydration: "Quick amount",
  sleep: "Sleep summary",
  mood: "How are you feeling?",
  symptoms: "What are you noticing?",
  caffeine: "Intake",
  workout: "Activity",
  medication: "Medication",
  vitals: "Reading",
  disruptor: "What happened?",
  // Unused at runtime: connection is excluded from the quick-pick row below
  // because its own ChoiceField chips already serve that role. Kept here
  // only because the Record is keyed by every ManualLogActionId.
  connection: "How did you connect?",
};

const DEFAULT_QUICK_PICKS: Record<ManualLogActionId, string | null> = {
  food: null,
  hydration: "500 ml",
  sleep: "Slept well",
  mood: "Calm",
  symptoms: "Nausea",
  caffeine: "Coffee",
  workout: "Walk",
  medication: null,
  vitals: "Weight",
  disruptor: null,
  connection: null,
};

// Seed the highlighted chip from the form's actual values so a pre-selected
// capture (e.g. a glucose reading) does not render its field under a stale
// default chip like "Weight". Matches on the field each chip patches, so no
// label guessing.
function initialQuickPick(
  actionId: ManualLogActionId,
  form: ManualLogForm,
  quickPicks: readonly ManualLogQuickPick[],
): string | null {
  const matchField = (key: keyof ManualLogForm): string | null =>
    quickPicks.find((pick) => pick.patch[key] !== undefined && pick.patch[key] === form[key])
      ?.label ?? null;
  if (actionId === "vitals") return matchField("vitalsMetric");
  if (actionId === "food") return matchField("foodMealType");
  return DEFAULT_QUICK_PICKS[actionId];
}

export function LogForm({
  actionId,
  form,
  onPatch,
}: {
  actionId: ManualLogActionId;
  form: ManualLogForm;
  onPatch: (patch: FormPatch) => void;
}) {
  const quickPicks = manualLogQuickPicks(actionId);
  const [selectedQuickPick, setSelectedQuickPick] = useState<string | null>(() =>
    initialQuickPick(actionId, form, quickPicks),
  );
  const moreWorkoutPicks = actionId === "workout" ? manualLogWorkoutMoreQuickPicks() : [];

  function selectQuickPick(pick: ManualLogQuickPick) {
    // Chips read as a radio group, so picking one withdraws the last one's
    // claim. Each writes only the dimension it names, so without this they
    // accumulate: "Low mood" then "Energized" would save a low mood alongside
    // high energy under the second chip's label.
    // This handler serves the more-workout list too, so look there as well:
    // a previous pick found in neither is a pick that never gets withdrawn.
    const previous = [...quickPicks, ...moreWorkoutPicks].find(
      (p) => p.label === selectedQuickPick,
    );
    const cleared: FormPatch = {};
    if (previous && previous.label !== pick.label) {
      const defaults = createManualLogForm();
      for (const key of Object.keys(previous.patch) as (keyof FormPatch)[]) {
        if (!(key in pick.patch)) {
          (cleared as Record<string, unknown>)[key] = defaults[key as keyof typeof defaults];
        }
      }
    }
    setSelectedQuickPick(pick.label);
    onPatch({ ...cleared, ...pick.patch });
  }

  function patchFromField(next: FormPatch) {
    setSelectedQuickPick(null);
    onPatch(next);
  }

  // A confounder carries no measurement, no free text, and no back-dating: it
  // is a structured acknowledgement whose window runs forward from now. So it
  // gets its own body (reason + duration) without the quick-picks, time, or
  // note fields every other log action shares.
  if (actionId === "disruptor") {
    return (
      <div className="space-y-7">
        <DisruptorFields form={form} onPatch={patchFromField} />
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {actionId !== "medication" && actionId !== "connection" ? (
        <ManualLogQuickPicks
          actionId={actionId}
          label={PRIMARY_PICK_LABELS[actionId]}
          picks={quickPicks}
          selected={selectedQuickPick}
          onSelect={selectQuickPick}
        />
      ) : null}
      {actionId === "workout" ? (
        <MoreWorkoutActivities
          picks={moreWorkoutPicks}
          selected={selectedQuickPick}
          onSelect={selectQuickPick}
        />
      ) : null}
      {actionId === "food" ? <FoodFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "hydration" ? <HydrationFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "sleep" ? <SleepFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "mood" ? <MoodFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "symptoms" ? <SymptomsFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "caffeine" ? <CaffeineFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "workout" ? <WorkoutFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "medication" ? <MedicationFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "vitals" ? <VitalsFields form={form} onPatch={patchFromField} /> : null}
      {actionId === "connection" ? <ConnectionFields form={form} onPatch={patchFromField} /> : null}
      <DateTimeField
        value={form.occurredAt}
        onChange={(occurredAt) => patchFromField({ occurredAt })}
      />
      <TextAreaField
        label="Context"
        value={form.note}
        onChange={(note) => patchFromField({ note })}
        placeholder={notePlaceholder(actionId)}
      />
    </div>
  );
}

function DisruptorFields({ form, onPatch }: FormFieldsProps) {
  return (
    <>
      <p className="text-sm leading-relaxed text-gray-600">
        Acknowledge a reading you could not control. We will ease off on the affected areas for a
        while, and your numbers stay exactly as they are.
      </p>
      <ChoiceField
        label="What happened?"
        value={form.confounderReason}
        options={CONFOUNDER_REASON_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        onChange={(value) => onPatch({ confounderReason: value as ConfounderReason })}
      />
      <ChoiceField
        label="For how long?"
        value={form.confounderDuration}
        options={CONFOUNDER_DURATION_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        onChange={(value) => onPatch({ confounderDuration: value as ConfounderDuration })}
      />
    </>
  );
}

function ConnectionFields({ form, onPatch }: FormFieldsProps) {
  return (
    <>
      <ChoiceField
        label="How did you connect?"
        value={form.connectionType}
        options={CONNECTION_TYPE_OPTIONS}
        onChange={(value) => onPatch({ connectionType: value as ConnectionType })}
      />
      <ChoiceField
        label="Who with? (optional)"
        value={form.connectionWho}
        options={CONNECTION_WHO_OPTIONS}
        // "(optional)" promises a member can clear this back out once picked.
        // ChoiceField's own onClick always reports the clicked option, so a
        // second click on the already-selected chip reads as "deselect" here
        // rather than "re-select" - scoped to this field only, since it is the
        // only (optional) ChoiceField in this file whose label implies
        // clearability (disruptor/workout/medication choices are required).
        onChange={(value) =>
          onPatch({ connectionWho: form.connectionWho === value ? "" : (value as ConnectionWho) })
        }
      />
      <RangeField
        label="How connected did you feel?"
        value={form.connectionRating}
        onChange={(connectionRating) => onPatch({ connectionRating })}
        minLabel="Distant"
        maxLabel="Close"
      />
    </>
  );
}

function FoodFields({ form, onPatch }: FormFieldsProps) {
  return (
    <>
      <aside className="rounded-2xl border border-mist bg-surface-tertiary/55 px-5 py-4 text-sm leading-relaxed text-gray-600">
        <p className="font-medium text-navy">Photo logging lives in the mobile app.</p>
        <p className="mt-1">
          For photo-based meal logging and richer food insights, use the HealthOS mobile app.
        </p>
      </aside>
      <TextField
        label="What did you have?"
        value={form.foodMeal}
        onChange={(foodMeal) => onPatch({ foodMeal })}
        placeholder="For example, salmon and roasted vegetables"
      />
    </>
  );
}

function HydrationFields({ form, onPatch }: FormFieldsProps) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <NumberField
        label="Amount"
        value={form.hydrationAmountMl}
        onChange={(hydrationAmountMl) => onPatch({ hydrationAmountMl })}
        suffix="ml"
        min="1"
      />
      <SelectField
        label="Type"
        value={form.hydrationBeverage}
        onChange={(hydrationBeverage) => onPatch({ hydrationBeverage })}
        options={["Water", "Bottle", "Electrolytes", "Other"]}
      />
    </div>
  );
}

function SleepFields({ form, onPatch }: FormFieldsProps) {
  const isNap = form.sleepKind === "nap";
  return (
    <div className="max-w-sm">
      {isNap ? (
        <NumberField
          label="Nap length"
          value={form.napMinutes}
          onChange={(napMinutes) => onPatch({ napMinutes })}
          suffix="min"
          min="1"
        />
      ) : (
        <NumberField
          label="Sleep length"
          value={form.sleepHours}
          onChange={(sleepHours) => onPatch({ sleepHours })}
          suffix="hours"
          min="0.5"
          step="0.5"
        />
      )}
    </div>
  );
}

function MoodFields({ form, onPatch }: FormFieldsProps) {
  return (
    <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
      <RangeField
        label="Mood"
        value={form.mood}
        onChange={(mood) => onPatch({ mood })}
        minLabel="Low"
        maxLabel="Bright"
      />
      <RangeField
        label="Energy"
        value={form.energy}
        onChange={(energy) => onPatch({ energy })}
        minLabel="Low"
        maxLabel="High"
      />
      <RangeField
        label="Stress"
        value={form.stress}
        onChange={(stress) => onPatch({ stress })}
        minLabel="Light"
        maxLabel="High"
      />
      <RangeField
        label="Focus"
        value={form.focus}
        onChange={(focus) => onPatch({ focus })}
        minLabel="Scattered"
        maxLabel="Steady"
      />
    </div>
  );
}

function SymptomsFields({ form, onPatch }: FormFieldsProps) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <TextField
        label="What are you noticing?"
        value={form.symptomLabel}
        onChange={(symptomLabel) => onPatch({ symptomLabel })}
        placeholder="For example, nausea"
      />
      <RangeField
        label="Severity"
        value={form.symptomSeverity}
        onChange={(symptomSeverity) => onPatch({ symptomSeverity })}
        min="1"
        max="10"
        minLabel="Mild"
        maxLabel="Intense"
      />
    </div>
  );
}

function CaffeineFields({ form, onPatch }: FormFieldsProps) {
  const isAlcohol = form.intakeType === "alcohol";
  const noDetail = form.intakeType === "none";

  return !noDetail ? (
    <div className="grid gap-5 sm:grid-cols-2">
      {isAlcohol ? (
        <NumberField
          label="Servings"
          value={form.alcoholServings}
          onChange={(alcoholServings) => onPatch({ alcoholServings })}
          min="0"
          step="1"
        />
      ) : (
        <>
          <NumberField
            label="Servings"
            value={form.caffeineCups}
            onChange={(caffeineCups) => onPatch({ caffeineCups })}
            suffix="cups"
            min="0"
            step="1"
          />
          <NumberField
            label="Caffeine"
            value={form.caffeineMg}
            onChange={(caffeineMg) => onPatch({ caffeineMg })}
            suffix="mg"
            min="0"
          />
        </>
      )}
    </div>
  ) : null;
}

function WorkoutFields({ form, onPatch }: FormFieldsProps) {
  return (
    <div className="space-y-6">
      <RangeField
        label="Duration"
        value={form.workoutDurationMinutes}
        onChange={(workoutDurationMinutes) => onPatch({ workoutDurationMinutes })}
        min="5"
        max="180"
        step="5"
        minLabel="5 min"
        maxLabel="3 hr"
        valueFormatter={(value) => `${value} min`}
        editorial
      />
      <ChoiceField
        label="Intensity"
        value={form.workoutIntensity}
        onChange={(workoutIntensity) =>
          onPatch({ workoutIntensity: workoutIntensity as ManualLogForm["workoutIntensity"] })
        }
        options={WORKOUT_INTENSITY_OPTIONS}
      />
    </div>
  );
}

function MoreWorkoutActivities({
  picks,
  selected,
  onSelect,
}: {
  picks: readonly ManualLogQuickPick[];
  selected: string | null;
  onSelect: (pick: ManualLogQuickPick) => void;
}) {
  return (
    <details className="group rounded-xl border border-mist bg-surface-tertiary/45 px-4 py-3.5 open:bg-surface-elevated">
      <summary
        aria-label="More activities"
        className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm text-navy marker:content-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-500"
      >
        <span>
          <span className="font-medium">More activities</span>
          <span className="ml-2 text-gray-500">Hiking, Pilates, dance, sports, and more</span>
        </span>
        <CaretDown
          size={17}
          weight="bold"
          aria-hidden="true"
          className="shrink-0 text-teal-700 transition-transform duration-200 ease-out group-open:rotate-180 motion-reduce:transition-none"
        />
      </summary>
      <div className="pt-5">
        <ManualLogQuickPicks
          actionId="workout"
          label="More activities"
          hideLabel
          picks={picks}
          selected={selected}
          onSelect={onSelect}
        />
      </div>
    </details>
  );
}

function MedicationFields({ form, onPatch }: FormFieldsProps) {
  const isTaken = form.medicationStatus === "taken";
  const knownMedication = MEDICATIONS[form.medication.trim().toLowerCase()];

  useEffect(() => {
    if (!knownMedication) return;
    const cadenceDays = String(knownMedication.cadenceDays);
    if (form.cadenceDays !== cadenceDays) onPatch({ cadenceDays });
  }, [form.cadenceDays, knownMedication, onPatch]);

  return (
    <>
      <MedicationPicker form={form} onPatch={onPatch} />
      <ChoiceField
        label="Status"
        value={form.medicationStatus}
        onChange={(medicationStatus) =>
          onPatch({ medicationStatus: medicationStatus as api.ManualMedicationStatus })
        }
        options={MEDICATION_STATUS_OPTIONS}
        columns={5}
        compact
      />
      {isTaken && knownMedication ? (
        <MedicationDosePicker
          medication={form.medication}
          value={form.doseAmount}
          onChange={(doseAmount) => onPatch({ doseAmount })}
        />
      ) : null}
      <div
        className={`grid gap-5 ${isTaken && !knownMedication ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
      >
        {isTaken && !knownMedication ? (
          <NumberField
            label="Dose"
            value={form.doseAmount}
            onChange={(doseAmount) => onPatch({ doseAmount })}
            suffix="mg"
            min="0.01"
            step="0.01"
          />
        ) : null}
        <SelectField
          label="Injection site"
          value={form.injectionSite}
          onChange={(injectionSite) => onPatch({ injectionSite })}
          options={["Abdomen", "Thigh", "Arm", "Not applicable"]}
        />
        {knownMedication ? (
          <MedicationCadence cadenceDays={knownMedication.cadenceDays} />
        ) : (
          <NumberField
            label="Cadence"
            value={form.cadenceDays}
            onChange={(cadenceDays) => onPatch({ cadenceDays })}
            suffix="days"
            min="1"
          />
        )}
      </div>
    </>
  );
}

function MedicationDosePicker({
  medication,
  value,
  onChange,
}: {
  medication: string;
  value: string;
  onChange: (doseAmount: string) => void;
}) {
  const doses = medicationDoses(medication);
  const selectedDose = Number(value);

  return (
    <fieldset>
      <legend className="mb-3 flex items-center gap-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
        <span className="shrink-0">Dose</span>
        <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-mist" />
      </legend>
      <div className="flex flex-wrap gap-2">
        {doses.map((dose) => {
          const selected = selectedDose === dose;
          return (
            <button
              key={dose}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(String(dose))}
              className={cn(
                "rounded-full border px-3.5 py-2 text-sm font-medium tabular-nums transition-[background-color,border-color,color,box-shadow] duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500",
                selected
                  ? "border-navy bg-navy text-white shadow-[0_10px_22px_-16px_color-mix(in_srgb,var(--color-navy)_80%,transparent)]"
                  : "border-mist bg-surface-elevated text-navy hover:border-teal-300 hover:bg-teal-50/45",
              )}
            >
              {dose} mg
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function MedicationCadence({ cadenceDays }: { cadenceDays: number }) {
  const cadence = cadenceDays === 1 ? "Once daily" : `Every ${cadenceDays} days`;
  return (
    <div>
      <p className="mb-3 flex items-center gap-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
        <span className="shrink-0">Cadence</span>
        <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-mist" />
      </p>
      <output className="flex min-h-11 items-center rounded-xl border border-mist bg-surface-tertiary/55 px-4 text-sm text-navy">
        {cadence}
      </output>
    </div>
  );
}

function MedicationPicker({ form, onPatch }: FormFieldsProps) {
  const picks = manualLogQuickPicks("medication");
  const [showChoices, setShowChoices] = useState(() => form.medication.trim().length === 0);
  const [memberChangedChoice, setMemberChangedChoice] = useState(false);
  const medication = form.medication.trim();
  const selectedPick = picks.find((pick) => pick.label.toLowerCase() === medication.toLowerCase());

  useEffect(() => {
    if (!memberChangedChoice && medication) setShowChoices(false);
  }, [medication, memberChangedChoice]);

  const details = [
    form.doseAmount.trim() ? `${form.doseAmount} mg` : null,
    form.cadenceDays.trim() ? `Every ${form.cadenceDays} days` : null,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" · ");

  function choose(pick: ManualLogQuickPick) {
    setMemberChangedChoice(true);
    setShowChoices(false);
    onPatch({
      ...pick.patch,
      cadenceDays: String(medicationCadence(pick.label)),
    });
  }

  return (
    <section>
      <div className="mb-3 flex items-center gap-4 font-mono text-xs font-semibold uppercase tracking-[0.15em] text-gray-500">
        <span className="shrink-0">Medication</span>
        <span aria-hidden="true" className="h-px min-w-0 flex-1 bg-mist" />
      </div>
      {showChoices ? (
        <div className="space-y-4">
          <ManualLogQuickPicks
            actionId="medication"
            label="Choose medication"
            picks={picks}
            selected={selectedPick?.label ?? null}
            onSelect={choose}
          />
          <TextField
            label="Another medication"
            value={form.medication}
            onChange={(nextMedication) => {
              setMemberChangedChoice(true);
              onPatch({ medication: nextMedication, doseAmount: "" });
            }}
            placeholder="For example, Wegovy"
          />
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-navy bg-navy px-5 py-4 text-white shadow-[0_16px_30px_-22px_color-mix(in_srgb,var(--color-navy)_80%,transparent)]">
          <div className="min-w-0">
            <p className="truncate text-lg font-medium">{medication}</p>
            {details ? (
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-teal-200">
                {details}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Choose another medication"
            aria-expanded="false"
            onClick={() => {
              setMemberChangedChoice(true);
              setShowChoices(true);
            }}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-white transition-colors hover:bg-white/12 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-300"
          >
            Change
            <CaretDown size={15} weight="bold" aria-hidden="true" />
          </button>
        </div>
      )}
    </section>
  );
}

type FormFieldsProps = { form: ManualLogForm; onPatch: (patch: FormPatch) => void };

function notePlaceholder(actionId: ManualLogActionId): string {
  const placeholders: Record<ManualLogActionId, string> = {
    food: "How did this meal feel for energy, appetite, or nourishment?",
    hydration: "Thirst, electrolytes, workout, or medication context",
    sleep: "What helped or got in the way?",
    mood: "What shaped this check-in?",
    symptoms: "Timing, medication, meal, or pattern",
    caffeine: "Timing, sleep impact, appetite, or context",
    workout: "Route, recovery, soreness, or anything notable",
    medication: "Side effects, appetite, timing, or dose-window context",
    vitals: "Device, posture, fasting state, or anything relevant",
    disruptor: "",
    connection: "What made this connection meaningful, or what got in the way?",
  };
  return placeholders[actionId];
}
